import type { WranglerItem } from "fileParsing/wrangler/types";
import { addNewData } from "@store/data/actions";
import type { FileProperties } from "@store/data/types";
import { addNewData as addNewWranglerData } from "@store/wrangler/actions";
import { getDataPointsLength } from "@utils/array";
import DataGenerator from "@utils/dataGenerator";
import type { AppDispatch } from "index";
import { action } from "typesafe-actions";
import { v4 } from "uuid";
import { EventTypes } from "webworker/BaseWorker";
import { addExportWorker, getExportWorkerByUUID } from "webworker/init";
import { deserializeOutputs, mapDataPointToPolicyAttributes } from "@/containers/Visuals/Sankey/parserProcessing";
import type { ApplicationState } from "../index";
import { FileDropActionTypes } from "./types";

/**
 * Triggers a file to be added to the store
 */
export function dropFile(files: File[]) {
  return (dispatch: AppDispatch) => {
    dispatch(action(FileDropActionTypes.FILE_ADD, files));
    dispatch(processNextFile());
  };
}

export function processNextFile() {
  return (dispatch: AppDispatch, getState: () => ApplicationState) => {
    // Only one file should be processing at a time
    const loadingFiles = getState().fileParsing.file.filter(({ loading, progress }) => !loading && progress === 0);
    if (loadingFiles.length > 0) {
      const nextFile = loadingFiles[0];
      dispatch(action(FileDropActionTypes.ITER_START, { id: nextFile.id }));
      const workerId = v4();
      console.log(`[ACTION] Attemting to parse file: ${nextFile.file.name} with worker: ${workerId}`);

      addExportWorker(workerId, nextFile.file).then(() => {
        dispatch(
          subscribeWorker(
            {
              fileName: nextFile.file.name,
              fileSize: nextFile.file.size,
            } as FileProperties,
            workerId,
            nextFile.id,
          ),
        );
        dispatch(processNextFile());
      });
    }
  };
}

function subscribeWorker(fileProperties: FileProperties, workerId: string, fileId: string) {
  return (dispatch: AppDispatch, getState: () => ApplicationState) => {
    document.addEventListener(`${workerId}_wrangler_${EventTypes.Progress}`, (event) => {
      const ev = event as CustomEvent;
      dispatch(
        action(FileDropActionTypes.ITER_PROGRESS, {
          id: fileId,
          progress: ev.detail ?? 0,
        }),
      );
    });
    document.addEventListener(
      `${workerId}_wrangler_${EventTypes.DataReady}`,
      async (event) => {
        const ev = event as CustomEvent<WranglerItem[]>;
        const worker = await getExportWorkerByUUID(workerId)?.getWrangler();
        if (worker) {
          // Don't trust VSCode, 'await' HAS an effect on this expression.
          const providerId = await worker.getProviderId();
          console.log(providerId);
          if (providerId === undefined) {
            console.error("[ACTION] Failed to parse");
            dispatch(
              action(FileDropActionTypes.ITER_FAILURE, {
                id: fileId,
                msg: "Failed to parse archive",
              }),
            );
            return;
          }
          // Don't trust VSCode, 'await' HAS an effect on this expression.
          const files = (await worker.getFiles().data) ?? [];
          const dataPoints = getDataPointsLength(files);
          console.log(
            `[ACTION] Successfully parsed ${files.length} files and ${dataPoints} data points from ${fileProperties.fileName}.`,
          );

          dispatch(addNewWranglerData(getState().data.counter, ev.detail));
          dispatch(addNewData(fileProperties, providerId, workerId));
          dispatch(
            action(FileDropActionTypes.ITER_SUCCESS, {
              id: fileId,
              fileProperties,
            }),
          );
        }
      },
      // invoke once after which the event listener will be removed
      { once: true },
    );
  };
}

export function loadSampleDataset() {
  return (dispatch: AppDispatch, getState: () => ApplicationState) => {
    const seed = getState().fileParsing.sampleDatasetId + 1;
    dispatch(action(FileDropActionTypes.ITER_SUCCESS_SAMPLE));
    const filename = `sample_data_${seed}`;
    const lang = getState().language.language;
    console.time("[ACTION] Generating Sample Dataset");
    console.log(`[ACTION] Generating Sample Dataset with settings, seed: ${seed} and lang: ${lang}`);
    const sampleData = DataGenerator.generate(seed, lang);

    // loading policy category, sharing, purpose and sensitivity clauses to sample data points
    const sampleOutput = deserializeOutputs(["sample"]).sample;
    const [sampleMapping, parserOutput] = sampleOutput;

    sampleData.forEach((file) => {
      file.data = file.data.map((dp) => mapDataPointToPolicyAttributes(dp, file, sampleMapping, parserOutput));
    });

    console.timeEnd("[ACTION] Generating Sample Dataset");
    console.log(
      "[ACTION] Genereated new sample sample with " +
        sampleData.length +
        " files and " +
        getDataPointsLength(sampleData) +
        " data points.",
    );
    const workerId = v4();
    addExportWorker(
      workerId,
      sampleData.map((file) => ({ ...file, zipName: filename })),
    ).then(() => {
      dispatch(
        addNewData(
          {
            fileName: filename,
            fileSize: 0,
          } as FileProperties,
          "sample",
          workerId,
        ),
      );
    });
  };
}

export function resetState() {
  return (dispatch: AppDispatch) => {
    dispatch(action(FileDropActionTypes.RESET_STATE));
  };
}

export function clearError() {
  return (dispatch: AppDispatch) => {
    dispatch(action(FileDropActionTypes.CLEAR_ERROR));
  };
}

export function clearLastFilename() {
  return (dispatch: AppDispatch) => {
    dispatch(action(FileDropActionTypes.CLEAR_FILENAME));
  };
}
